import { faChevronDown, faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as Tabs from '@radix-ui/react-tabs'
import {
  TokenMedia,
  useAttributes,
  useCollections,
  useDynamicTokens,
  useTokenActivity,
} from '@reservoir0x/reservoir-kit-ui'
import { paths } from '@reservoir0x/reservoir-sdk'
import { ActivityFilters } from 'components/token/ActivityFilters'
import { MobileActivityFilters } from 'components/common/MobileActivityFilters'
import Layout from 'components/Layout'
import { Anchor, Box, Button, Flex, Grid, Text } from 'components/primitives'
import { Dropdown } from 'components/primitives/Dropdown'
import { TabsContent, TabsList, TabsTrigger } from 'components/primitives/Tab'
import AttributeCard from 'components/token/AttributeCard'
import { PriceData } from 'components/token/PriceData'
import RarityRank from 'components/token/RarityRank'
import { TokenActions } from 'components/token/TokenActions'
import { TokenActivityTable } from 'components/token/ActivityTable'
import { TokenInfo } from 'components/token/TokenInfo'
import { ToastContext } from 'context/ToastContextProvider'
import { useENSResolver, useMounted } from 'hooks'
import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { NORMALIZE_ROYALTIES } from 'pages/_app'
import { useContext, useEffect, useState } from 'react'
import { jsNumberForAddress } from 'react-jazzicon'
import Jazzicon from 'react-jazzicon/dist/Jazzicon'
import { useMediaQuery } from 'react-responsive'
import { DefaultChain } from 'utils/chains'
import { useAuthWallets } from 'hooks/auth/useAuthWallets'
import fetcher, { headers } from 'utils/fetcher'
import titleCase from 'utils/titleCase'
import { Head } from 'components/Head'
import { OffersTable } from 'components/token/OffersTable'
import { ListingsTable } from 'components/token/ListingsTable'
import Breadcrumb from 'components/token/Breadcrumb'
import { isTokenSoulbound } from 'utils/token'
import SoulboundBadge from 'components/custom/badges/SoulboundBadge'
import TokenDescription from 'components/token/TokenDescription'
import AuthOwnershipWalletGate from 'feature/auth/gates/AuthOwnershipWalletGate'
import {
  getContractAddressesForSystem,
  getReservoirChainFromContract,
} from 'config/routes'
import { getSystemIdChains, getSystemIdSupportedChains } from 'utils/multichain'
import { MultichainBids } from 'components/mergeLiquidity/MultichainBids'
import { MultichainListings } from 'components/mergeLiquidity/MultichainListings'
import { sortTokenData } from 'utils/mergeChainV2'
import { createDataHandler } from 'utils/mergeLiquidity'
import React from 'react'
import { verifyShipCollection } from 'utils/collection'
import { useMergedTokenData } from 'hooks/useMergedTokenData'
import { zeroAddress } from 'viem'
import { InfoBanner } from 'components/custom/banners/InfoBanner'
import { DISABLED_TOKEN_ID_SWEEPS } from 'utils/constants'

type Props = InferGetServerSidePropsType<typeof getServerSideProps>

type ActivityTypes = Exclude<
  NonNullable<
    NonNullable<
      Exclude<Parameters<typeof useTokenActivity>['1'], boolean>
    >['types']
  >,
  string
>

const IndexPage: NextPage<Props> = ({ systemId, ssr }) => {
  const systemIdPieces = systemId ? systemId.toString().split(':') : []
  const collectionId = systemIdPieces[0]
  const id = systemIdPieces[1]
  const router = useRouter()
  const { addToast } = useContext(ToastContext)
  const { ownershipPrivyWallet } = useAuthWallets()

  const isMounted = useMounted()
  const { ownershipWallet } = useAuthWallets()
  const isSmallDevice = useMediaQuery({ maxWidth: 900 }) && isMounted
  const [tabValue, setTabValue] = useState('info')

  const [activityFiltersOpen, setActivityFiltersOpen] = useState(true)
  const [activityTypes, setActivityTypes] = useState<ActivityTypes>(['sale'])
  const [activityNames, setActivityNames] = useState<string[]>(['sale'])
  const isShipsCollection = verifyShipCollection(collectionId)

  const { contract, defaultReservoirChain } = getSystemIdChains(
    collectionId ?? ''
  )
  // Find token with collection that has a current owner
  const collectionTokens =
    ssr?.merged?.tokens?.flatMap((holder) => holder.tokens) ?? []
  const registeredToken =
    collectionTokens.find(
      (collectionToken) =>
        collectionToken?.token?.owner !== zeroAddress &&
        collectionToken?.token?.owner !== null
    ) ?? ssr?.merged?.allTokensData

  const collectionContract = isShipsCollection
    ? registeredToken?.token?.contract
    : contract
  const chainId = isShipsCollection
    ? registeredToken?.token?.chainId
    : defaultReservoirChain?.id || 1
  const [offers, setOffers] = useState<any[]>([])
  const [listings, setListings] = useState<any[]>([])

  // NOTE: Keep for token media, description which should be identical (for game items)
  const { data: tokens, mutate } = useDynamicTokens(
    {
      tokens: [`${collectionContract}:${id}`],
      includeAttributes: true,
      includeTopBid: true,
      includeQuantity: true,
    },
    {
      fallbackData: [ssr.tokens ? ssr.tokens : {}],
    },
    chainId
  )

  const token = tokens && tokens[0] ? tokens[0] : null

  const is1155 = token?.token?.kind === 'erc1155'
  const isSoulbound = isTokenSoulbound(token)

  const { data: collections } = useCollections(
    {
      includeSecurityConfigs: true,
      includeMintStages: true,
      id: token?.token?.collection?.id,
    },
    {
      fallbackData: [ssr.collection ? ssr.collection : {}],
    },
    chainId
  )
  const supportedSystemIdChains = getSystemIdSupportedChains(collectionId)
  const collection = collections && collections[0] ? collections[0] : null

  // First token offer/listing in TokenActions
  // Helps to determine if offer/listing is oracle order and requires gas to be canceled
  const offer = offers && offers[0] ? offers[0] : undefined
  const listing = listings && listings[0] ? listings[0] : undefined

  const isTokenSweepDisabled = Boolean(
    token?.token &&
      DISABLED_TOKEN_ID_SWEEPS.includes(Number(token?.token?.tokenId))
  )

  const attributesData = useAttributes(collectionId)

  const userTokenMerged = useMergedTokenData(collectionId, id, 'user', {})

  let countOwned = is1155
    ? Number(userTokenMerged.data[0]?.ownership?.tokenCount || 0)
    : token?.token?.owner?.toLowerCase() ===
      ownershipWallet?.address?.toLowerCase()
    ? 1
    : 0

  const isOwner = countOwned > 0
  const owner = isOwner ? ownershipWallet?.address : token?.token?.owner
  const { displayName: ownerFormatted } = useENSResolver(token?.token?.owner)

  const tokenName = `${token?.token?.name || `#${token?.token?.tokenId}`}`

  const hasAttributes =
    token?.token?.attributes && token?.token?.attributes.length > 0

  const handleAllOffers = createDataHandler(setOffers)
  const handleAllListings = createDataHandler(setListings)

  const trigger = (
    <div>
      <Button
        color="gray3"
        size="small"
        css={{
          justifyContent: 'space-between',
          width: '336px',
          px: '$2',
          py: '$2',
        }}
      >
        {isSmallDevice ? null : (
          <Text style="body1">
            {activityNames.map(titleCase).join(', ') || 'All Events'}
          </Text>
        )}
        <Text css={{ color: '$slate10' }}>
          <FontAwesomeIcon icon={faChevronDown} width={16} height={16} />
        </Text>
      </Button>
    </div>
  )

  useEffect(() => {
    let tab = tabValue
    const hasAttributesTab = isMounted && isSmallDevice && hasAttributes
    if (hasAttributesTab) {
      tab = 'attributes'
    } else {
      tab = isTokenSweepDisabled ? 'listings' : 'activity'
    }

    let deeplinkTab: string | null = null
    if (typeof window !== 'undefined') {
      const params = new URL(window.location.href).searchParams
      deeplinkTab = params.get('tab')
    }

    if (deeplinkTab) {
      switch (deeplinkTab) {
        case 'attributes':
          if (hasAttributesTab) {
            tab = 'attributes'
          }
          break
        case 'info':
          tab = 'info'
          break
        case 'activity':
          tab = 'activity'
          break
        case 'listings':
          tab = 'listings'
          break
        case 'offers':
          tab = 'offers'
          break
      }
    }
    setTabValue(tab)
  }, [isSmallDevice])

  useEffect(() => {
    const updatedUrl = new URL(`${window.location.origin}${router.asPath}`)
    updatedUrl.searchParams.set('tab', tabValue)
    router.replace(updatedUrl, undefined, {
      shallow: true,
    })
  }, [tabValue])

  const pageTitle = token?.token?.name
    ? token.token.name
    : `${token?.token?.tokenId} - ${token?.token?.collection?.name}`

  const base64EncodedToken = btoa(encodeURIComponent(JSON.stringify(token)))

  const mintData = collection?.mintStages?.find(
    (stage) => stage.kind === 'public'
  )

  const mintPriceDecimal = mintData?.price?.amount?.decimal
  const mintCurrency = mintData?.price?.currency?.symbol?.toUpperCase()

  const mintPrice =
    typeof mintPriceDecimal === 'number' &&
    mintPriceDecimal !== null &&
    mintPriceDecimal !== undefined
      ? mintPriceDecimal === 0
        ? 'Free'
        : `${mintPriceDecimal} ${mintCurrency}`
      : undefined

  return (
    <Layout>
      <Head
        ogImage={`/api/og/token?token=${encodeURIComponent(
          base64EncodedToken
        )}`}
        title={pageTitle}
        description={collection?.description as string}
        metatags={
          <>
            {token?.market?.floorAsk?.price?.amount?.native && (
              <>
                <meta
                  property="fc:frame:button:2"
                  content={`Buy ${
                    token.market.floorAsk.price.amount.native
                  } ${token.market.floorAsk.price.currency?.symbol?.toUpperCase()}`}
                />
                <meta property="fc:frame:button:2:action" content="link" />
                <meta
                  property="fc:frame:button:2:target"
                  content={`${process.env.NEXT_PUBLIC_HOST_URL}${router.asPath}`}
                />
              </>
            )}
            <meta
              name="twitter:image"
              content={`/api/og/token?token=${encodeURIComponent(
                base64EncodedToken
              )}`}
            />
          </>
        }
      />
      <AuthOwnershipWalletGate>
        <Flex
          justify="center"
          css={{
            maxWidth: 1320,
            mt: 10,
            pb: 100,
            marginLeft: 'auto',
            marginRight: 'auto',
            px: '$1',
            gap: 20,
            flexDirection: 'column',
            alignItems: 'center',
            '@md': {
              mt: 48,
              px: '$3',
              flexDirection: 'row',
              gap: 40,
              alignItems: 'flex-start',
            },
            '@lg': {
              gap: 80,
            },
          }}
        >
          {supportedSystemIdChains.map((supportedChain) => (
            <React.Fragment key={supportedChain.id}>
              <MultichainBids
                systemId={collectionId}
                collection={supportedChain}
                tokenId={id}
                onDataFetched={handleAllOffers}
              />
              <MultichainListings
                address={ownershipWallet?.address}
                systemId={collectionId}
                collection={supportedChain}
                tokenId={id}
                onDataFetched={handleAllListings}
              />
            </React.Fragment>
          ))}
          <Flex
            direction="column"
            css={{
              maxWidth: '100%',
              flex: 1,
              width: '100%',
              '@md': { maxWidth: 445 },
              '@lg': { maxWidth: 520 },
              '@xl': { maxWidth: 620 },
              position: 'relative',
              '@sm': {
                '>button': {
                  height: 0,
                  opacity: 0,
                  transition: 'opacity .3s',
                },
              },
              ':hover >button': {
                opacity: 1,
                transition: 'opacity .3s',
              },
            }}
          >
            <Box
              css={{
                backgroundColor: '$gray3',
                borderRadius: 8,
                '@sm': {
                  button: {
                    height: 0,
                    opacity: 0,
                    transition: 'opacity .3s',
                  },
                },
                ':hover button': {
                  opacity: 1,
                  transition: 'opacity .3s',
                },
              }}
            >
              <TokenMedia
                token={token?.token}
                videoOptions={{ autoPlay: true, muted: true }}
                imageResolution={'large'}
                style={{
                  width: '100%',
                  height: 'auto',
                  minHeight: isMounted && isSmallDevice ? 300 : 445,
                  borderRadius: 8,
                  overflow: 'hidden',
                }}
                onRefreshToken={() => {
                  // mutate?.()
                  addToast?.({
                    title: 'Refresh token',
                    description: 'Request to refresh this token was accepted.',
                  })
                }}
              />
            </Box>

            {isShipsCollection && (
              <InfoBanner text="Purchasing a Ship does not include Skins or Attachments." />
            )}

            {token?.token?.description && (
              <TokenDescription description={token?.token?.description} />
            )}

            {token?.token?.attributes && !isSmallDevice && (
              <Grid
                css={{
                  maxWidth: '100%',
                  width: '100%',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '$3',
                  mt: 24,
                }}
              >
                {token?.token?.attributes?.map((attribute: any) => (
                  <AttributeCard
                    key={`${attribute.key}-${attribute.value}`}
                    attribute={attribute}
                    collectionTokenCount={collection?.tokenCount || 0}
                    collectionId={collection?.id}
                  />
                ))}
              </Grid>
            )}
          </Flex>
          <Flex
            direction="column"
            css={{
              flex: 1,
              px: '$3',
              width: '100%',
              '@md': {
                px: 0,
                maxWidth: '60%',
                overflow: 'hidden',
              },
            }}
          >
            <Breadcrumb
              router={router}
              collection={collection}
              token={token}
              contract={contract}
              id={id}
              countOwned={countOwned}
            />
            <Flex align="center" direction="row" css={{ gap: '$2' }}>
              <Text style="h3" css={{ wordBreak: 'break-all' }}>
                {tokenName}
              </Text>
              {isSoulbound === true ? <SoulboundBadge /> : null}
              <div style={{ flex: 1 }}></div>
              <RarityRank
                token={token}
                collection={collection}
                collectionAttributes={attributesData?.data}
              />
            </Flex>
            {token && (
              <>
                {is1155 && countOwned > 0 && (
                  <Flex align="center" css={{ mt: '$2' }}>
                    <Text style="subtitle3" color="subtle" css={{ mr: '$2' }}>
                      You own {countOwned}
                    </Text>
                    {ownershipWallet && (
                      <Link
                        href={`/portfolio/${ownershipWallet.address || ''}`}
                        legacyBehavior={true}
                      >
                        <Anchor
                          color="primary"
                          weight="normal"
                          css={{ ml: '$1', fontSize: 12 }}
                        >
                          Sell
                        </Anchor>
                      </Link>
                    )}
                  </Flex>
                )}
                {!is1155 && owner && (
                  <Flex align="center" css={{ mt: '$2' }}>
                    <Text style="subtitle3" color="subtle" css={{ mr: '$2' }}>
                      Owner
                    </Text>
                    <Jazzicon
                      diameter={16}
                      seed={jsNumberForAddress(owner || '')}
                    />
                    <Link href={`/portfolio/${owner}`} legacyBehavior={true}>
                      <Anchor
                        color="primary"
                        weight="normal"
                        css={{ ml: '$1' }}
                      >
                        {isMounted ? ownerFormatted : ''}
                      </Anchor>
                    </Link>
                  </Flex>
                )}
                <PriceData
                  token={ssr?.merged?.allTokensData}
                  systemId={collectionId}
                />
                {isMounted && !isTokenSweepDisabled && (
                  <Box>
                    <TokenActions
                      token={ssr?.merged?.allTokensData}
                      collection={collection}
                      offer={offer}
                      listing={listing}
                      isOwner={isOwner}
                      mutate={mutate}
                      account={ownershipPrivyWallet}
                    />
                    {is1155 ? (
                      <InfoBanner text="Set a quantity of 10 or less to maximize purchase success." />
                    ) : null}
                  </Box>
                )}
                <Tabs.Root
                  value={tabValue}
                  onValueChange={(value) => setTabValue(value)}
                  style={{
                    paddingRight: isSmallDevice ? 0 : 15,
                  }}
                >
                  <TabsList
                    css={{
                      overflowX: isSmallDevice ? 'scroll' : 'unset',
                    }}
                  >
                    {isMounted && isSmallDevice && hasAttributes && (
                      <TabsTrigger value="attributes">Attributes</TabsTrigger>
                    )}
                    <TabsTrigger value="info">Info</TabsTrigger>
                    <TabsTrigger value="activity">Activity</TabsTrigger>
                    <TabsTrigger value="listings">Listings</TabsTrigger>
                    <TabsTrigger value="offers">Offers</TabsTrigger>
                  </TabsList>
                  <TabsContent value="attributes">
                    {token?.token?.attributes && (
                      <Grid
                        css={{
                          gap: '$3',
                          mt: 24,
                          gridTemplateColumns: '1fr',
                          '@sm': {
                            gridTemplateColumns: '1fr 1fr',
                          },
                        }}
                      >
                        {token?.token?.attributes?.map((attribute: any) => (
                          <AttributeCard
                            key={`${attribute.key}-${attribute.value}`}
                            attribute={attribute}
                            collectionTokenCount={collection?.tokenCount || 0}
                            collectionId={collection?.id}
                          />
                        ))}
                      </Grid>
                    )}
                  </TabsContent>
                  <TabsContent value="info">
                    {collection && (
                      <TokenInfo token={token} collection={collection} />
                    )}
                  </TabsContent>
                  <TabsContent
                    value="activity"
                    css={{ mr: -15, position: 'relative' }}
                  >
                    {isSmallDevice ? (
                      <MobileActivityFilters
                        activityTypes={activityTypes}
                        setActivityTypes={setActivityTypes}
                      />
                    ) : (
                      <Box css={{ width: 'fit-content' }}>
                        <Dropdown trigger={trigger}>
                          <ActivityFilters
                            open={activityFiltersOpen}
                            setOpen={setActivityFiltersOpen}
                            activityTypes={activityTypes}
                            activityNames={activityNames}
                            setActivityNames={setActivityNames}
                            setActivityTypes={setActivityTypes}
                          />
                        </Dropdown>
                      </Box>
                    )}
                    <TokenActivityTable
                      tokenId={id}
                      activityTypes={activityTypes}
                    />
                  </TabsContent>
                  <TabsContent value="listings">
                    <ListingsTable
                      tokenId={id}
                      address={ownershipWallet?.address}
                      is1155={is1155}
                      isOwner={isOwner}
                    />
                  </TabsContent>
                  <TabsContent value="offers" css={{ mr: -15, width: '100%' }}>
                    <OffersTable
                      tokenId={id}
                      address={ownershipWallet?.address}
                      is1155={is1155}
                      isOwner={isOwner}
                    />
                  </TabsContent>
                </Tabs.Root>
              </>
            )}
          </Flex>
        </Flex>
      </AuthOwnershipWalletGate>
    </Layout>
  )
}

type SSRProps = {
  collection?:
    | paths['/collections/v7']['get']['responses']['200']['schema']
    | null
  tokens?: paths['/tokens/v6']['get']['responses']['200']['schema'] | null
  merged?: {
    tokens: paths['/tokens/v6']['get']['responses']['200']['schema'][]
    collections: paths['/collections/v7']['get']['responses']['200']['schema'][]
    // allTokensData: paths['/tokens/v6']['get']['responses']['200']['schema']
    allTokensData: any
  }
}

export const getServerSideProps: GetServerSideProps<{
  systemId?: string
  ssr: SSRProps
}> = async ({ params, res }) => {
  const systemIdParam = params?.systemId
    ? params.systemId.toString().split(':')
    : []
  let systemId = systemIdParam[0]
  const tokenId = systemIdParam[1]

  if (!tokenId) {
    res.writeHead(302, { Location: `/nft/${systemId}` })
    res.end()
    return {
      props: {
        systemId: systemId as string,
        ssr: {
          collection: null,
          tokens: null,
          merged: {
            tokens: [],
            collections: [],
            allTokensData: null,
          },
        },
      },
    }
  }
  const chainContractIds = getContractAddressesForSystem(systemId)

  const allTokens: paths['/tokens/v6']['get']['responses']['200']['schema'][] =
    []
  const allCollections: paths['/collections/v7']['get']['responses']['200']['schema'][] =
    []

  // From systemId -> chainContractIds[]
  for (const contract of chainContractIds) {
    let tokens: SSRProps['tokens'] = null
    let collection: SSRProps['collection'] = null
    const { reservoirBaseUrl } =
      getReservoirChainFromContract(contract) || DefaultChain

    const tokensQuery: paths['/tokens/v6']['get']['parameters']['query'] = {
      tokens: [`${contract}:${tokenId}`],
      includeAttributes: true,
      includeTopBid: true,
      normalizeRoyalties: NORMALIZE_ROYALTIES,
      includeDynamicPricing: true,
    }
    try {
      const tokensPromise = fetcher(
        `${reservoirBaseUrl}/tokens/v6`,
        tokensQuery,
        headers
      )

      const tokensResponse = await tokensPromise
      tokens = tokensResponse.data
        ? (tokensResponse.data as Props['ssr']['tokens'])
        : {}
      if (tokens) {
        allTokens.push(tokens)
      }

      let collectionQuery: paths['/collections/v7']['get']['parameters']['query'] =
        {
          id: tokens?.tokens?.[0]?.token?.collection?.id,
          normalizeRoyalties: NORMALIZE_ROYALTIES,
          //@ts-ignore: Removes Blur
          excludeEOA: true,
        }

      const collectionsPromise = fetcher(
        `${reservoirBaseUrl}/collections/v7`,
        collectionQuery,
        headers
      )

      const collectionsResponse = await collectionsPromise
      collection = collectionsResponse.data
        ? (collectionsResponse.data as Props['ssr']['collection'])
        : {}
      if (collection) {
        allCollections.push(collection)
      }

      res.setHeader(
        'Cache-Control',
        'public, s-maxage=30, stale-while-revalidate=60'
      )
    } catch (e) {
      console.log(e)
    }
  }

  const flattenedAllTokens = allTokens.flatMap((tokens) => tokens.tokens)
  const mergedTokens = sortTokenData(flattenedAllTokens)
  const allTokensData = mergedTokens[0] !== undefined ? mergedTokens[0] : null

  // Ensure topBid is not undefined
  if (allTokensData && allTokensData.market) {
    allTokensData.market.topBid = allTokensData.market.topBid || null
  }

  return {
    props: {
      systemId: params?.systemId as string,
      ssr: {
        collection: allCollections[0] || null,
        tokens: allTokens[0] || null,
        merged: {
          tokens: allTokens,
          collections: allCollections,
          allTokensData,
        },
      },
    },
  }
}

export default IndexPage
